import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Controls, Panel, ReactFlow, ReactFlowProvider, ViewportPortal, applyEdgeChanges, applyNodeChanges, getNodesBounds, getViewportForBounds, useNodesInitialized, useReactFlow, useStore } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIsRefreshedDashboardFlag } from "../../hooks/useAppConfigFlags";
import { isIndianaChargerStation, useIsIndianaChargerStationFlag } from "../../hooks/useIsIndianaChargerStationFlag";
import { getNetworkEdges, getNetworkNodes } from "../../pages/Projects/ProjectDetails.helper";
import { NetworkColumn } from "../../pages/Projects/networkDiagramLayout";
import useAppConfigStore from "../../store/useAppConfigStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import APIError from "../APIError/APIError";
import { AssetNode } from "../CustomNodes/AssetNode";
import { ChargeStationNode } from "../CustomNodes/ChargeStationNode";
import { NodeIconColor } from "../CustomNodes/CustomNodeEnums";
import { UtilityPowerNode } from "../CustomNodes/UtilityPowerNode";
import "./NetworkDiagram.css";
const API_STATUS_ERROR = "error";
const API_STATUS_PENDING = "pending";
const API_STATUS_SUCCESS = "success";
// Opts the Live Dashboard's power node into the refreshed layout without exposing the
// dashboard flag inside the shared node component.
const RefreshedUtilityPowerNode = ({ data }) => (_jsx(UtilityPowerNode, { data: { ...data, isRefreshedLayout: true } }));
const ASSET_SOURCE_ESS = "ESS";
const ASSET_SOURCE_MACHINE = "MACHINE";
const ASSET_SOURCE_ENERGY_SOURCE = "ENERGY_SOURCE";
const ASSET_SOURCE_CHARGING_STATION = "CHARGING_STATION";
const TRANSIT_AT_SOURCE = "AtSource";
const TRANSIT_AT_SITE = "AtSite";
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
// Fit-to-view tuning. The diagram is drawn on a fixed coordinate canvas (see
// getNetworkNodes + NetworkDiagram.css): the "Charging Area"/"Work Area" column headers
// sit ~50px above the first node row and the decorative "Transit" corridor spans
// y≈100→900. None of those are React Flow nodes, so getNodesBounds() omits them — we union
// the measured node bounds with this fixed frame so the fit always includes the headers +
// corridor, keeps the whole canvas visible, and never over-zooms a small site.
// Frame the header band and the full column span. The vertical extent below the header comes
// from the nodes themselves, so short diagrams are not padded out by the decorative transit
// corridor (which is allowed to run past the bottom edge, as in the design).
const DIAGRAM_FRAME_BOUNDS = { height: 1030, width: 1200, x: 60, y: -80 };
// Small margin around the fitted diagram — just enough that the leftmost/rightmost node
// isn't flush against the pane edge. The refreshed grid is packed tighter, so it needs more.
const FIT_VIEW_PADDING = 0.005;
const REFRESHED_FIT_VIEW_PADDING = 0.03;
// Lowered from React Flow's default (0.5) so large sites with many stacked assets can
// shrink far enough to fit; the default clamps zoom-out and clips tall diagrams.
const MIN_ZOOM = 0.05;
// Cap the fit zoom (matches React Flow's default) so small sites don't blow up.
const MAX_ZOOM = 2;
// Gap in px between the pane top and the top of the diagram once it is top-aligned.
const FIT_TOP_GAP = 12;
const STATUS_CONTAINER_CLASSES = "flex justify-center items-center h-full min-h-[40vh]";
const NO_DATA_TEXT = "No Data Available";
/** Smallest rectangle fully containing both inputs — used to grow the fit bounds so the
 *  fixed header/corridor decorations (which are not React Flow nodes) stay in view. */
export const unionRects = (first, second) => {
    const x = Math.min(first.x, second.x);
    const y = Math.min(first.y, second.y);
    const width = Math.max(first.x + first.width, second.x + second.width) - x;
    const height = Math.max(first.y + first.height, second.y + second.height) - y;
    return { height, width, x, y };
};
// Refreshed layout column grid, taken from the design (Figma 37:63131 — a 512px-wide frame).
// The legacy grid spreads the same columns over ~1200px of mostly-empty canvas, so the
// fit-to-view always zooms out to ~0.5 and the diagram reads tiny. Re-packing only the
// occupied columns into these widths/gaps reproduces the design exactly for the designed
// asset mix and still collapses the gaps for every other mix.
const REFRESHED_START_X = 13;
const REFRESHED_BAND_WIDTH = 84;
const REFRESHED_CARD_WIDTH = 70;
const REFRESHED_STATION_WIDTH = 73;
const REFRESHED_COLUMN_GAP = 47;
const REFRESHED_GAP_BEFORE_BAND = 24;
const REFRESHED_GAP_AFTER_BAND = 20;
// Grid Power tucks straight onto the WattHub column in the design rather than sitting in its
// own gutter.
const REFRESHED_GAP_AFTER_POWER = 0;
// Title row above the nodes (y 16→32), used as the fit frame so short diagrams aren't padded
// out by the full-height transit corridor.
const REFRESHED_HEADER_HEIGHT = 40;
const REFRESHED_BAND_HEIGHT = 563;
// Top of the station/power column, just clear of the column title row.
const REFRESHED_STATION_TOP_Y = 48;
const refreshedColumnWidth = (columnX) => {
    if (columnX === NetworkColumn.TRANSIT)
        return REFRESHED_BAND_WIDTH;
    if (columnX === NetworkColumn.POWER || columnX === NetworkColumn.CHARGING_STATION)
        return REFRESHED_STATION_WIDTH;
    return REFRESHED_CARD_WIDTH;
};
const refreshedGapBefore = (previousX, columnX) => {
    if (previousX === NetworkColumn.POWER)
        return REFRESHED_GAP_AFTER_POWER;
    if (columnX === NetworkColumn.TRANSIT)
        return REFRESHED_GAP_BEFORE_BAND;
    if (previousX === NetworkColumn.TRANSIT)
        return REFRESHED_GAP_AFTER_BAND;
    return REFRESHED_COLUMN_GAP;
};
/** Maps every occupied legacy column x to its packed x. Transit and Work Area always claim a
 *  slot — both corridors are shown even with zero current nodes, as in the design — so their
 *  titles are never left centering on a fallback position. */
export const packRefreshedColumns = (nodeXs) => {
    const columns = Array.from(new Set([...nodeXs, NetworkColumn.TRANSIT, NetworkColumn.WORK_AREA])).sort((a, b) => a - b);
    const packed = new Map();
    let cursor = REFRESHED_START_X;
    let previousX = null;
    columns.forEach((columnX) => {
        if (previousX !== null)
            cursor += refreshedGapBefore(previousX, columnX);
        packed.set(columnX, cursor);
        cursor += refreshedColumnWidth(columnX);
        previousX = columnX;
    });
    return packed;
};
/** Center x across every packed column right of the transit band. The "at site" assets are
 *  spread over two legacy columns (x 850 and the Work Area slot at x 1050) that read as one
 *  "Work Area" region in the design, so the title centers over both rather than just the
 *  Work Area slot. */
export const workAreaRegionCenterX = (packedColumns) => {
    let left = null;
    let right = null;
    packedColumns.forEach((packedX, originalX) => {
        if (originalX <= NetworkColumn.TRANSIT)
            return;
        const packedRight = packedX + refreshedColumnWidth(originalX);
        left = left === null ? packedX : Math.min(left, packedX);
        right = right === null ? packedRight : Math.max(right, packedRight);
    });
    if (left === null || right === null)
        return packedColumns.get(NetworkColumn.WORK_AREA) ?? REFRESHED_START_X;
    return (left + right) / 2;
};
/** Right edge of the rightmost packed column. The Work Area slot is often empty, so its
 *  title sits outside getNodesBounds() and would be clipped unless the fit frame spans it. */
export const packedColumnsRight = (packedColumns) => {
    let right = REFRESHED_START_X;
    packedColumns.forEach((packedX, originalX) => {
        right = Math.max(right, packedX + refreshedColumnWidth(originalX));
    });
    return right;
};
const NetworkDiagramStatus = ({ networkApiStatus }) => {
    if (networkApiStatus === API_STATUS_PENDING) {
        return (_jsx("div", { className: STATUS_CONTAINER_CLASSES, "data-test": "loading-indicator", children: _jsx(CatLoadingIndicator, {}) }));
    }
    return _jsx("div", { className: `${STATUS_CONTAINER_CLASSES} text-xl text-center`, children: NO_DATA_TEXT });
};
const NetworkDiagramUI = ({ assetTrendsDetails, networkApiStatus, onAssetSelect, onToggleScreen, projectId, refetchNetworkDetails, replayTimeline, replayTimelineValue }) => {
    const { assetChargeActivityData, networkDiagramData, networkEdges, networkNodes } = useProjectDetailsStore((state) => ({
        assetChargeActivityData: state.assetChargeActivityData,
        networkDiagramData: state.networkDiagramData,
        networkEdges: state.networkEdges,
        networkNodes: state.networkNodes
    }));
    const { appConfig } = useAppConfigStore((state) => ({ appConfig: state.appConfig }));
    const showGhostAssetPhaseOne = appConfig?.release_1_1?.showGhostAssetPhaseOne;
    // The Historic replay panel mounts this same diagram, so the refreshed canvas (packed
    // columns, dot grid, hidden legend, reduced controls) is scoped to the Live tab only.
    // Node styling stays on the shared flag so a node never renders two ways.
    const isRefreshedLayout = useIsRefreshedDashboardFlag() && !replayTimeline;
    const [nodes, setNodes] = useState(networkNodes);
    const [edges, setEdges] = useState(networkEdges);
    const [assetNetworkNodesDetails, setAssetNetworkNodesDetails] = useState([]);
    const [assetNetworkEdgesDetails, setAssetNetworkEdgesDetails] = useState([]);
    const { projectDetails } = useProjectDetailsStore((state) => ({
        projectDetails: state.projectDetails
    }));
    const tooltipShowOnlyAssetName = replayTimeline > 0;
    // Get timezone with fallback pattern to handle API field naming inconsistency
    // API may return timeZone (camelCase with capital Z), timezone (lowercase), or offset
    // Using bracket notation to access potentially undefined property at runtime
    const projectTimezone = projectDetails?.["timeZone"] ||
        projectDetails?.timezone ||
        projectDetails?.offset;
    const reactFlow = useReactFlow();
    // React Flow keeps the measured pane size in its store (updated via an internal
    // ResizeObserver), so subscribing here gives resize-driven refits for free — including
    // entering/exiting full screen and window resizes.
    const paneWidth = useStore((state) => state.width);
    const paneHeight = useStore((state) => state.height);
    // True once every node has been measured. Fitting only after this keeps the bounds
    // accurate, and because it resets to false on remount it re-arms the fit whenever the
    // diagram is re-created (e.g. entering or exiting full screen).
    const nodesInitialized = useNodesInitialized();
    const chargeStationNode = nodes.find((n) => n.type === "chargeStationNode");
    const chargeStationHeadingLabel = chargeStationNode?.data?.label;
    const indianaStation = projectDetails?.AssetDetails?.find((asset) => isIndianaChargerStation(asset.assetName, asset.assetSourceType));
    const isIndianaStation = useIsIndianaChargerStationFlag(indianaStation?.assetName, indianaStation?.assetSourceType);
    const watthubDisplayLabel = isIndianaStation ? "Charging Area" : chargeStationHeadingLabel;
    const packedColumns = useMemo(() => (isRefreshedLayout ? packRefreshedColumns(nodes.map((node) => node.position.x)) : null), [isRefreshedLayout, nodes]);
    const transitBandX = packedColumns?.get(NetworkColumn.TRANSIT) ?? REFRESHED_START_X;
    const workAreaTitleX = packedColumns
        ? workAreaRegionCenterX(packedColumns)
        : transitBandX + REFRESHED_CARD_WIDTH / 2;
    // Refreshed design pulls every occupied column into the packed grid and shows the WattHub
    // label inside its own box rather than as a floating title above it.
    // The station/power column also sits nearer the header than the shared lane cursors place
    // it; both move by one delta so their connector geometry is unchanged.
    const stationLift = useMemo(() => {
        if (!packedColumns)
            return 0;
        const stationYs = nodes.filter((node) => node.type === "chargeStationNode").map((node) => node.position.y);
        return stationYs.length === 0 ? 0 : Math.min(...stationYs) - REFRESHED_STATION_TOP_Y;
    }, [nodes, packedColumns]);
    const displayNodes = useMemo(() => {
        if (!packedColumns)
            return nodes;
        return nodes.map((node) => {
            const packedX = packedColumns.get(node.position.x);
            const centredX = node.position.x === NetworkColumn.TRANSIT && packedX !== undefined
                ? packedX + (REFRESHED_BAND_WIDTH - REFRESHED_CARD_WIDTH) / 2
                : packedX;
            const isStationColumn = node.type === "chargeStationNode" || node.type === "utilityPowerNode";
            const nextY = isStationColumn ? node.position.y - stationLift : node.position.y;
            const nextX = centredX ?? node.position.x;
            const moved = nextX === node.position.x && nextY === node.position.y
                ? node
                : { ...node, position: { x: nextX, y: nextY } };
            return moved.id === chargeStationNode?.id
                ? { ...moved, data: { ...moved.data, displayLabel: watthubDisplayLabel } }
                : moved;
        });
    }, [nodes, packedColumns, stationLift, chargeStationNode?.id, watthubDisplayLabel]);
    const frameBounds = useMemo(() => packedColumns
        ? {
            height: REFRESHED_HEADER_HEIGHT,
            width: packedColumnsRight(packedColumns) - REFRESHED_START_X,
            x: REFRESHED_START_X,
            y: 0
        }
        : DIAGRAM_FRAME_BOUNDS, [packedColumns]);
    // Fit the whole diagram — nodes plus the fixed header/corridor frame — into the pane.
    // Read nodes from React Flow (not local state) so the bounds use their *measured* sizes;
    // together with the useNodesInitialized() gate on the refit effect below, this avoids a
    // first fit that treats not-yet-measured nodes as zero-size points (which could clip
    // node UI/labels). Reading imperatively also keeps fitDiagram referentially stable, so
    // the refit runs on resize / node-count changes rather than every replay-slider tick.
    const fitDiagram = useCallback(() => {
        const currentNodes = reactFlow.getNodes();
        if (currentNodes.length === 0 || paneWidth === 0 || paneHeight === 0)
            return;
        const bounds = unionRects(getNodesBounds(currentNodes), frameBounds);
        const padding = isRefreshedLayout ? REFRESHED_FIT_VIEW_PADDING : FIT_VIEW_PADDING;
        const { x, zoom } = getViewportForBounds(bounds, paneWidth, paneHeight, MIN_ZOOM, MAX_ZOOM, padding);
        // getViewportForBounds centres the diagram vertically, which floats it away from the
        // header. Keep its width-fit zoom + horizontal centring, but re-pin the vertical offset
        // so the top of the diagram sits just below the pane top, as close to the header as
        // the small stabilizing gap (FIT_TOP_GAP) allows.
        const y = FIT_TOP_GAP - bounds.y * zoom;
        void reactFlow.setViewport({ x, y, zoom });
    }, [reactFlow, paneWidth, paneHeight, frameBounds, isRefreshedLayout]);
    const nodeTypes = useMemo(() => ({
        assetNode: AssetNode,
        chargeStationNode: ChargeStationNode,
        utilityPowerNode: isRefreshedLayout ? RefreshedUtilityPowerNode : UtilityPowerNode
    }), [isRefreshedLayout]);
    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
    }, [setNodes]);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), [setEdges]);
    // Refit once the nodes are measured, and again whenever the pane resizes (window
    // resize, entering or exiting full screen) or the node count changes (load, add,
    // remove). This is the only fit — React Flow's built-in fitView is intentionally off
    // so it can't override this with a nodes-only (zoomed-in) fit after a remount/reload.
    const nodeCount = nodes.length;
    useEffect(() => {
        if (!nodesInitialized || nodeCount === 0 || paneWidth === 0 || paneHeight === 0)
            return undefined;
        const raf = requestAnimationFrame(() => fitDiagram());
        return () => cancelAnimationFrame(raf);
    }, [fitDiagram, nodesInitialized, nodeCount, paneWidth, paneHeight]);
    const onNodeClick = (_event, select) => {
        projectDetails?.AssetDetails.forEach((data) => {
            if (data?.assetId === select?.id) {
                onAssetSelect(data);
            }
        });
    };
    useEffect(() => {
        if (replayTimeline) {
            const assetNetworkNodes = [], assetNetworkEdges = [], chargingSourceAssets = [];
            const networkDiagramMap = new Map((networkDiagramData || []).map((n) => [n.assetId, n.assetTransitDirection]));
            projectDetails?.AssetDetails.forEach((assetDetail) => {
                if (!showGhostAssetPhaseOne && assetDetail.assetSourceType?.toUpperCase() === "GHOST")
                    return;
                // Create a copy so assetNetworkNodesDetails is independent of the store's
                // AssetDetails array — live refetches must not bleed into replay state.
                const assetDetailCopy = { ...assetDetail };
                if (assetDetailCopy.assetSourceType === ASSET_SOURCE_ESS) {
                    assetDetailCopy["assetTransitDirection"] = TRANSIT_AT_SOURCE;
                }
                else if (assetDetailCopy.assetSourceType === ASSET_SOURCE_MACHINE) {
                    assetDetailCopy["assetTransitDirection"] = TRANSIT_AT_SITE;
                }
                else if (assetDetailCopy.assetSourceType === ASSET_SOURCE_ENERGY_SOURCE) {
                    // Methanol is fixed at one location (source or site).
                    // The connections API (networkDiagramData) is the authoritative source
                    // for that fixed position. Activity data only overrides during transport.
                    assetDetailCopy["assetTransitDirection"] =
                        networkDiagramMap.get(assetDetailCopy.assetId) || TRANSIT_AT_SOURCE;
                }
                assetDetailCopy.activity = ""; // empty string = no recorded activity (sentinel)
                assetDetailCopy["chargingSource"] = null;
                assetNetworkNodes.push(assetDetailCopy);
                if (assetDetailCopy.assetSourceType === ASSET_SOURCE_CHARGING_STATION) {
                    chargingSourceAssets.push(assetDetailCopy);
                }
            });
            assetChargeActivityData.forEach((assetChargeActivity) => {
                if (assetChargeActivity.chargingSource) {
                    assetNetworkEdges.push(assetChargeActivity);
                }
            });
            const updatedNetworkNodes = getNetworkNodes(assetNetworkNodes, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, tooltipShowOnlyAssetName, projectTimezone);
            const updatedNetworkEdges = getNetworkEdges(chargingSourceAssets);
            setNodes(updatedNetworkNodes);
            setEdges(updatedNetworkEdges);
            setAssetNetworkNodesDetails(assetNetworkNodes);
            setAssetNetworkEdgesDetails(chargingSourceAssets);
        }
        else {
            setNodes(networkNodes);
            setEdges(networkEdges);
        }
    }, [networkNodes, networkEdges, replayTimeline, networkDiagramData, showGhostAssetPhaseOne]);
    useEffect(() => {
        if (assetNetworkNodesDetails.length > 0) {
            const updatedNetworkEdges = assetNetworkEdgesDetails.map((obj) => ({ ...obj }));
            const assetNetworkNodes = assetNetworkNodesDetails.map((obj) => ({ ...obj }));
            assetChargeActivityData.forEach((networkEdge) => {
                const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", assetId = "", assetSourceType = "", assetTransitDirection = "", chargingSource = "" } = networkEdge;
                if (assetSourceType !== ASSET_SOURCE_CHARGING_STATION &&
                    activityStartTimeStamp &&
                    activityEndTimeStamp) {
                    // Build a full ISO timestamp for the current slider position using
                    // the activity's own date ("YYYY-MM-DD"). This prevents cross-day
                    // false matches when assetChargeActivityData spans multiple days —
                    // the old time-of-day-only comparison would incorrectly match a
                    // July 4 activity when replaying July 3 data at the same wall time.
                    const activityDate = activityStartTimeStamp.substring(0, 10);
                    const sliderSecs = Number(replayTimelineValue);
                    const h = Math.floor(sliderSecs / SECONDS_PER_HOUR);
                    const m = Math.floor((sliderSecs % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
                    const s = Math.floor(sliderSecs % SECONDS_PER_MINUTE);
                    const replayTs = `${activityDate}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
                    // ISO string comparison works correctly for same-format timestamps
                    const startTs = activityStartTimeStamp.substring(0, 19);
                    const endTs = activityEndTimeStamp.substring(0, 19);
                    if (replayTs >= startTs && replayTs <= endTs) {
                        const networkNodeIndex = assetNetworkNodes.findIndex((networkNode) => networkNode.assetId === assetId);
                        if (networkNodeIndex !== -1) {
                            assetNetworkNodes[networkNodeIndex].activity = activity;
                            assetNetworkNodes[networkNodeIndex].activityStartTimeStamp = activityStartTimeStamp;
                            assetNetworkNodes[networkNodeIndex].activityEndTimeStamp = activityEndTimeStamp;
                            if (assetTransitDirection) {
                                assetNetworkNodes[networkNodeIndex].assetTransitDirection = assetTransitDirection;
                            }
                            if (chargingSource) {
                                updatedNetworkEdges.push(networkEdge);
                                if (assetSourceType?.toUpperCase() === ASSET_SOURCE_MACHINE) {
                                    const csAssetIndex = assetNetworkNodes.findIndex((networkNode) => networkNode.assetId === chargingSource);
                                    if (csAssetIndex !== -1 &&
                                        assetTransitDirection &&
                                        assetNetworkNodes[csAssetIndex].assetSourceType?.toUpperCase() ===
                                            ASSET_SOURCE_ESS) {
                                        assetNetworkNodes[csAssetIndex].assetTransitDirection = TRANSIT_AT_SITE;
                                    }
                                }
                            }
                        }
                    }
                }
            });
            const updatedNetworkNodes = getNetworkNodes(assetNetworkNodes, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, tooltipShowOnlyAssetName, projectTimezone);
            setNodes(updatedNetworkNodes);
            setEdges(getNetworkEdges(updatedNetworkEdges));
        }
    }, [replayTimelineValue, assetNetworkNodesDetails]);
    if (networkApiStatus === API_STATUS_ERROR) {
        return _jsx(APIError, { refreshHandler: refetchNetworkDetails });
    }
    return (_jsx(_Fragment, { children: networkApiStatus === API_STATUS_PENDING ||
            (networkApiStatus === API_STATUS_SUCCESS && networkNodes.length === 0) ? (_jsx(NetworkDiagramStatus, { networkApiStatus: networkApiStatus })) : (_jsxs(ReactFlow, { className: isRefreshedLayout ? "network-diagram-dot-grid !bg-transparent" : "bg-gray-100", "data-test": "React-Flow", deleteKeyCode: null, edges: edges, minZoom: MIN_ZOOM, nodeTypes: nodeTypes, nodes: displayNodes, nodesDraggable: false, onEdgesChange: onEdgesChange, onNodeClick: onNodeClick, onNodesChange: onNodesChange, proOptions: { hideAttribution: true }, children: [networkNodes.length > 0 && (_jsxs(_Fragment, { children: [!isRefreshedLayout && (_jsxs(Panel, { className: "bg-gray-100 text-black border-none w-40 h-26 text-left p-2 network-diagram-legend-container", "data-test": "Panel", position: "bottom-left", children: [_jsx("div", { className: "text-gray-500 w-full text-xs font-sans pl-2 pb-2", "data-test": "legend", children: "Legend" }), _jsx("div", { "data-test": "legend-item-idle", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "idle", children: "Idle" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.IDLE}`, "data-test": "legend-dot-idle" })] }) }), _jsx("div", { "data-test": "legend-item-occupied", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "occupied", children: "Occupied" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.OCCUPIED}`, "data-test": "legend-dot-occupied" })] }) }), _jsx("div", { "data-test": "legend-item-discharging", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "discharging", children: "Discharging" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.DISCHARGING}`, "data-test": "legend-dot-discharging" })] }) }), _jsx("div", { "data-test": "legend-item-charging", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "charging", children: "Charging" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.CHARGING}`, "data-test": "legend-dot-charging" })] }) }), _jsx("div", { "data-test": "legend-item-transporting", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "transporting", children: "Transporting" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.TRANSPORTING}`, "data-test": "legend-dot-transporting" })] }) }), _jsx("div", { "data-test": "legend-item-working", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "working", children: "Working" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.WORKING}`, "data-test": "legend-dot-working" })] }) }), _jsx("div", { "data-test": "legend-item-faulted", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "faulted", children: "Faulted" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.FAULTED}`, "data-test": "legend-dot-faulted" })] }) }), _jsx("div", { "data-test": "legend-item-off", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "off", children: "Off" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.OFF}`, "data-test": "legend-dot-off" })] }) }), _jsx("div", { "data-test": "legend-item-unavailable", children: _jsxs("div", { className: "w-full font-sans text-sm", children: [_jsx("div", { className: "ml-5 inline-block relative bottom-0.5", "data-test": "unavailable", children: "Unavailable" }), _jsx("div", { className: `w-2 h-2 float-left rounded-full m-1 ${NodeIconColor.UNAVAILABLE}`, "data-test": "legend-dot-unavailable" })] }) })] })), _jsx(ViewportPortal, { children: _jsxs(_Fragment, { children: [!isRefreshedLayout && chargeStationHeadingLabel && (_jsx("div", { className: "network-watthub", children: _jsx(CatHeading, { "data-test": "watthub-title", variant: "title", children: watthubDisplayLabel }) })), isRefreshedLayout ? (
                                    /* eslint-disable no-inline-styles/no-inline-styles --
                                       these sit on the packed column grid, so their x is
                                       computed rather than a static utility class. */
                                    _jsxs(_Fragment, { children: [_jsx("div", { className: "absolute top-0 -translate-x-1/2 bg-[#f2f2f2] opacity-50", "data-test": "transit-band", style: {
                                                    height: REFRESHED_BAND_HEIGHT,
                                                    left: transitBandX + REFRESHED_BAND_WIDTH / 2,
                                                    width: REFRESHED_BAND_WIDTH
                                                } }), _jsx("div", { className: "absolute top-4 -translate-x-1/2 whitespace-nowrap text-center font-['Roboto'] text-xs font-semibold leading-4 text-[#565656]", "data-test": "transit-title", style: { left: transitBandX + REFRESHED_BAND_WIDTH / 2 }, children: "Transit" }), _jsx("div", { className: "absolute top-4 -translate-x-1/2 whitespace-nowrap text-center font-['Roboto'] text-xs font-semibold leading-4 text-[#565656]", "data-test": "work-area-title", style: { left: workAreaTitleX }, children: "Work Area" })] })) : (
                                    /* eslint-enable no-inline-styles/no-inline-styles */
                                    _jsxs(_Fragment, { children: [_jsx("div", { className: "network-transit", children: _jsx("div", { className: "network-transit-title", children: _jsx(CatHeading, { "data-test": "transit-title", variant: "title", children: "Transit" }) }) }), _jsx("div", { className: "network-work-area", children: _jsx(CatHeading, { "data-test": "work-area-title", variant: "title", children: "Work Area" }) })] }))] }) }, projectId)] })), isRefreshedLayout ? (_jsx(Controls, { position: "bottom-right", showFitView: false, showInteractive: false })) : (_jsx(Controls, { onFitView: () => onToggleScreen(), position: "bottom-right", showFitView: !replayTimeline, showInteractive: false }))] })) }));
};
const NetworkDiagram = (props) => {
    return (_jsx(ReactFlowProvider, { children: _jsx(NetworkDiagramUI, { ...props }) }));
};
export default memo(NetworkDiagram);
